import type {
  LoanCreate,
  LoanPaymentCreate,
  LoanPaymentPublic,
  LoanPublic,
  LoanSummaryPublic,
} from '../type/loan';
import type { CashPaymentType, PaymentType } from '../type/payment';
import type { Loan, LoanOverview, LoanPayment } from '@/types';
import { toThreeDigits } from '@/lib/utils';
import { LastPaymentMapper } from './policy';

export const LoanPaymentMapper = (payment: LoanPaymentPublic): LoanPayment => ({
  id: String(payment.id),
  forMonth: payment.for_month,
  paidOn: payment.paid_on,
  principal: payment.principal,
  interest: payment.interest,
  balanceAfter: payment.balance_after,
  paymentType: payment.payment_type,
});

export const LoanAPIResponseMapper = (loan: LoanPublic): Loan => ({
  id: String(loan.id),
  loanNo: loan.loan_no,
  policyId: String(loan.policy_id),
  policyNo: loan.policy_no,
  cardNo: loan.card_no || undefined,
  memberId: String(loan.user_id),
  memberNo: loan.member_id != null ? toThreeDigits(loan.member_id) : undefined,
  memberName: loan.user_name ?? '',
  memberPhone: loan.user_phone ?? '',
  amount: loan.amount,
  givenOn: loan.given_on,
  givenAs: loan.given_as,
  status: loan.status,
  principalPaid: loan.principal_paid,
  outstanding: loan.outstanding,
  interestPaid: loan.interest_paid ?? 0,
  nextDueMonth: loan.next_due_month ?? undefined,
  thisMonth: loan.this_month ?? undefined,
  closedMonth: loan.closed_month ?? undefined,
  lastPayment: LastPaymentMapper(loan.last_payment),
  agreementUrl: loan.agreement_url ?? undefined,
  nextPayment: loan.next_payment
    ? {
        forMonth: loan.next_payment.for_month,
        minPrincipal: loan.next_payment.min_principal,
        interest: loan.next_payment.interest,
      }
    : undefined,
  payments: (loan.payments ?? []).map(LoanPaymentMapper),
});

export const LoanOverviewMapper = (summary?: LoanSummaryPublic | null): LoanOverview => ({
  active: summary?.active ?? 0,
  activeGiven: summary?.active_given ?? 0,
  activeMembers: summary?.active_members ?? 0,
  outstanding: summary?.outstanding ?? 0,
  principalRepaid: summary?.principal_repaid ?? 0,
  interestEarned: summary?.interest_earned ?? 0,
  dueThisMonth: summary?.due_this_month ?? 0,
  dueAmount: summary?.due_amount ?? 0,
  paidThisMonth: summary?.paid_this_month ?? 0,
  closed: summary?.closed ?? 0,
  closedRepaid: summary?.closed_repaid ?? 0,
  all: summary?.all ?? 0,
});

export const LoanAPIRequestMapper = (input: {
  policyId: string;
  amount: number;
  givenAs: CashPaymentType;
  agreementUrl: string;
}): LoanCreate => ({
  policy_id: Number(input.policyId),
  amount: input.amount,
  given_as: input.givenAs,
  agreement_url: input.agreementUrl,
});

export const LoanPaymentRequestMapper = (input: {
  principal: number;
  paymentType: PaymentType;
}): LoanPaymentCreate => ({
  principal: input.principal,
  payment_type: input.paymentType,
});
